// Shared WebRTC mesh primitives for 1:N audio/video meetings. The
// "mesh" here is the classic everybody-talks-to-everybody pattern that
// works fine for small calls (≤ 6 participants); SFU-backed calls are a
// follow-up.
//
// This module is UI-agnostic: it owns local media, peer connections,
// SDP offer/answer plumbing, and ICE handling, but exposes the result
// as plain state so both mail-web's compact Gmail-style panel and
// connect-web's full-screen Teams-style room can render whatever they
// want with the same backing data.
//
// Signaling is delegated via the `MeetingMeshSignaling` adapter. Each
// app passes in its own SignalR-or-otherwise client; this file knows
// nothing about @microsoft/signalr or any specific BFF.
import { useCallback, useEffect, useRef, useState } from "react";

const ICE_SERVERS: RTCIceServer[] = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" }
];

export interface MeshPeer {
    connectionId: string;
    userId: string;
    email: string;
    displayName: string;
    stream: MediaStream | null;
    audio: boolean;
    video: boolean;
}

export interface MeetingMeshSignaling {
    /** Currently connected to the BFF? Mesh waits for true before joining. */
    isConnected: boolean;
    joinMeeting: (meetingId: string) => Promise<void>;
    leaveMeeting: (meetingId: string) => Promise<void>;
    sendOffer: (toConnectionId: string, offer: RTCSessionDescriptionInit) => Promise<void>;
    sendAnswer: (toConnectionId: string, answer: RTCSessionDescriptionInit) => Promise<void>;
    sendIceCandidate: (toConnectionId: string, candidate: RTCIceCandidateInit) => Promise<void>;
    toggleMedia: (mediaType: "audio" | "video", enabled: boolean) => Promise<void>;
    /** Subscribe to incoming hub events. Returns an unsubscribe function. */
    on: (handlers: MeshHandlers) => () => void;
}

export interface MeshHandlers {
    onParticipants?: (peers: Array<{ connectionId: string; userId: string; email: string; displayName: string }>) => void;
    onUserJoined?: (peer: { connectionId: string; userId: string; email: string; displayName: string }) => void;
    onUserLeft?: (connectionId: string) => void;
    onOffer?: (fromConnectionId: string, offer: RTCSessionDescriptionInit) => void;
    onAnswer?: (fromConnectionId: string, answer: RTCSessionDescriptionInit) => void;
    onIceCandidate?: (fromConnectionId: string, candidate: RTCIceCandidateInit) => void;
    onMediaToggled?: (connectionId: string, mediaType: "audio" | "video", enabled: boolean) => void;
}

export interface UseMeetingMeshResult {
    localStream: MediaStream | null;
    peers: Record<string, MeshPeer>;
    micOn: boolean;
    cameraOn: boolean;
    mediaError: string | null;
    toggleMic: () => void;
    toggleCamera: () => void;
    /** Tear down peer connections + release camera/mic. Call this on leave. */
    teardown: () => void;
}

export interface UseMeetingMeshOptions {
    meetingId: string;
    signaling: MeetingMeshSignaling;
    /** Default { audio: true, video: true }. Pass { video: false } for audio-only calls. */
    constraints?: MediaStreamConstraints;
    /** Called when a peer joins mid-call (used by callers to play a tone, etc). */
    onPeerJoined?: (peer: { userId: string; displayName: string }) => void;
}

export function useMeetingMesh({
    meetingId,
    signaling,
    constraints = { audio: true, video: true },
    onPeerJoined
}: UseMeetingMeshOptions): UseMeetingMeshResult {
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [peers, setPeers] = useState<Record<string, MeshPeer>>({});
    const [micOn, setMicOn] = useState(true);
    const [cameraOn, setCameraOn] = useState(constraints.video !== false);
    const [mediaError, setMediaError] = useState<string | null>(null);

    const peerConnections = useRef<Record<string, RTCPeerConnection>>({});
    const localStreamRef = useRef<MediaStream | null>(null);
    const joinedRef = useRef(false);
    const onPeerJoinedRef = useRef(onPeerJoined);
    onPeerJoinedRef.current = onPeerJoined;

    // ----- Local media acquisition -----
    useEffect(() => {
        let cancelled = false;
        const acquire = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                setLocalStream(stream);
                localStreamRef.current = stream;
            } catch (err) {
                setMediaError(err instanceof Error ? err.message : "Camera / microphone blocked.");
            }
        };
        void acquire();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Peer-connection helpers -----

    const upsertPeer = useCallback((connectionId: string, p: { userId: string; email: string; displayName: string }) => {
        setPeers((prev) => ({
            ...prev,
            [connectionId]: prev[connectionId] ?? {
                connectionId,
                userId: p.userId,
                email: p.email,
                displayName: p.displayName,
                stream: null,
                audio: true,
                video: true
            }
        }));
    }, []);

    const teardownPeer = useCallback((connectionId: string) => {
        const pc = peerConnections.current[connectionId];
        if (pc) {
            pc.close();
            delete peerConnections.current[connectionId];
        }
        setPeers((prev) => {
            const next = { ...prev };
            delete next[connectionId];
            return next;
        });
    }, []);

    const ensurePeerConnection = useCallback((remoteConnectionId: string): RTCPeerConnection => {
        if (peerConnections.current[remoteConnectionId]) return peerConnections.current[remoteConnectionId]!;

        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        peerConnections.current[remoteConnectionId] = pc;

        if (localStreamRef.current) {
            for (const track of localStreamRef.current.getTracks()) {
                pc.addTrack(track, localStreamRef.current);
            }
        }

        pc.ontrack = (event) => {
            const [remoteStream] = event.streams;
            setPeers((prev) => ({
                ...prev,
                [remoteConnectionId]: prev[remoteConnectionId]
                    ? { ...prev[remoteConnectionId]!, stream: remoteStream ?? null }
                    : prev[remoteConnectionId]!
            }));
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                void signaling.sendIceCandidate(remoteConnectionId, event.candidate.toJSON());
            }
        };

        return pc;
    }, [signaling]);

    const initiateOfferTo = useCallback(async (remoteConnectionId: string) => {
        const pc = ensurePeerConnection(remoteConnectionId);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await signaling.sendOffer(remoteConnectionId, offer);
    }, [ensurePeerConnection, signaling]);

    // ----- Wire up signaling handlers -----
    useEffect(() => {
        const unsubscribe = signaling.on({
            onParticipants: (list) => {
                for (const peer of list) {
                    upsertPeer(peer.connectionId, peer);
                    void initiateOfferTo(peer.connectionId);
                }
            },
            onUserJoined: (peer) => {
                upsertPeer(peer.connectionId, peer);
                onPeerJoinedRef.current?.({ userId: peer.userId, displayName: peer.displayName });
            },
            onUserLeft: (connectionId) => {
                teardownPeer(connectionId);
            },
            onOffer: async (fromConnectionId, offer) => {
                const pc = ensurePeerConnection(fromConnectionId);
                await pc.setRemoteDescription(offer);
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                await signaling.sendAnswer(fromConnectionId, answer);
            },
            onAnswer: async (fromConnectionId, answer) => {
                const pc = peerConnections.current[fromConnectionId];
                if (pc) await pc.setRemoteDescription(answer);
            },
            onIceCandidate: async (fromConnectionId, candidate) => {
                const pc = peerConnections.current[fromConnectionId];
                if (pc && candidate) {
                    try { await pc.addIceCandidate(candidate); }
                    catch { /* ICE before remoteDescription — ignore */ }
                }
            },
            onMediaToggled: (connectionId, mediaType, enabled) => {
                setPeers((prev) => {
                    const peer = prev[connectionId];
                    if (!peer) return prev;
                    return {
                        ...prev,
                        [connectionId]: {
                            ...peer,
                            audio: mediaType === "audio" ? enabled : peer.audio,
                            video: mediaType === "video" ? enabled : peer.video
                        }
                    };
                });
            }
        });
        return unsubscribe;
    }, [signaling, upsertPeer, teardownPeer, ensurePeerConnection, initiateOfferTo]);

    // ----- Auto-join once both hub + media are ready -----
    useEffect(() => {
        if (joinedRef.current) return;
        if (!signaling.isConnected) return;
        if (!localStreamRef.current) return;
        joinedRef.current = true;
        void signaling.joinMeeting(meetingId);
    }, [signaling, meetingId, localStream]);

    // ----- Cleanup -----
    const teardown = useCallback(() => {
        if (joinedRef.current) {
            void signaling.leaveMeeting(meetingId);
            joinedRef.current = false;
        }
        Object.values(peerConnections.current).forEach((pc) => pc.close());
        peerConnections.current = {};
        localStreamRef.current?.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
        setLocalStream(null);
        setPeers({});
    }, [meetingId, signaling]);

    useEffect(() => () => {
        teardown();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- UI actions -----

    const toggleMic = useCallback(() => {
        const next = !micOn;
        setMicOn(next);
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = next; });
        }
        void signaling.toggleMedia("audio", next);
    }, [micOn, signaling]);

    const toggleCamera = useCallback(() => {
        const next = !cameraOn;
        setCameraOn(next);
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = next; });
        }
        void signaling.toggleMedia("video", next);
    }, [cameraOn, signaling]);

    return {
        localStream,
        peers,
        micOn,
        cameraOn,
        mediaError,
        toggleMic,
        toggleCamera,
        teardown
    };
}
